import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    SelectChangeEvent,
    TextField,
    Toolbar,
    Typography,
    useMediaQuery,
    Switch,
    Tooltip,
} from "@mui/material";

import "./styles.css";

import { Toast } from "primereact/toast";
import { ChangeEvent, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { useLocation } from "react-router-dom";
import { formatString, setToastRefToOpen } from "../../../functions/functions";

import {
    CustomerSettingsType,
    SelectedParametersType,
    Timezone,
    UserType,
} from "../../../types/types";

import { BadgeOutlined, CloseOutlined, HelpOutlineOutlined, SaveOutlined, SendOutlined, SettingsOutlined } from "@mui/icons-material";
import { userAtom } from "../../../atoms/atoms";
import { useRecoilValue } from "recoil";

import updateSettingsByCustomer from "../../../resources/Api/customerParameter/updateSettingsByCustomer";
import Rmodal from "../../../components/Rmodal";
import { MapContainer, Marker, Popup, TileLayer, Tooltip as LeafletTooltip } from "react-leaflet";
import findAllPlacesWithIqLocation from "../../../resources/Api/location/findAllPlacesWithIqLocation";
import { icon } from "leaflet";
import findAllTimezoneListResponse from '../../../resources/Api/timezone/findAllTimezoneListResponse';
import { isImpersonationLocked } from "../../../utils/impersonation";

const style: React.CSSProperties = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    background: "#fff",
    border: "none",
    borderRadius: "12px",
    color: "black",
    boxShadow: "0px 10px 30px rgba(0,0,0,0.1)",
    padding: "16px",
};

const isValidIdleMinutes = (value: string) => {
    const minutes = Number(value);
    return value.trim() !== "" && Number.isInteger(minutes) && minutes >= 1 && minutes <= 1440;
};

const SettingsPage = ({ isHiddenMenu }: { isHiddenMenu: boolean }) => {
    // atoms
    const getCurrentUser = useRecoilValue<UserType>(userAtom);

    // states
    const [showEvents, setShowEvents] = useState<boolean>(true);
    const [withSound, setWithSound] = useState<boolean>(true);
    const [showDesktopEvents, setShowDeskTopEvents] = useState<boolean>(true);
    const [notifyCnhExpireDate, setNotifyCnhExpireDate] = useState<boolean>(true);
    const [sendAutomaticCommands, setSendAutomaticCommands] = useState<boolean>(true);
    const [daysBefore, setDaysBefore] = useState<string>("7");
    const [useIdleAlert, setUseIdleAlert] = useState<boolean>(false);
    const [idleAlertMinutes, setIdleAlertMinutes] = useState<string>("5");
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [useTimezone, setUseTimezone] = useState<boolean>(false);
    const [timezones, setTimezones] = useState<Timezone[]>([]);
    const [timezoneId, setTimezoneId] = useState<number | null>(null);
    // const timezoneSelected = useMemo(() => timezones.find(t => t.id === timezoneId), [timezoneId, timezones])

    const [predictionResults, setPredictionResults] = useState<Array<any>>([]);
    const [query, setQuery] = useState<string>("");
    const [canSeach, setCanSearch] = useState<boolean>(false);
    const [typingTimer, setTypingTimer] = useState<any>(0);

    const [useInterestPoint, setUseInterestPoint] = useState<boolean>(false);
    const [useTransparentBackground, setUseTransparentBackground] = useState<boolean>(false);
    const [useLocalMap, setUseLocalMap] = useState<boolean>(false);
    const [useSpeedLimit, setUseSpeedLimit] = useState<boolean>(false);
    const [localLat, setLocalLat] = useState<number>(0);
    const [localLng, setLocalLng] = useState<number>(0);
    const [speedLimit, setSpeedLimit] = useState<number>(0);

    // funcionalities states
    // const [isOnline, setIsOnline] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [validationText, setTextValidation] = useState<string>("");
    const [isFieldsValid, setFieldsIsValid] = useState<boolean>(true);
    const [statusMessage, setStatusMessage] = useState<
        "warn" | "success" | "info" | "error" | undefined
    >("warn");
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [modalVisible, setModalVisible] = useState<boolean>(false);

    const matches = useMediaQuery("(max-width:430px)");

    // refs
    const toastRef = useRef<Toast>(null);
    const toastModalRef = useRef<Toast>(null);
    const mapRef = useRef<any>(null);
    const autcompleteRef = useRef<any>(null);

    // cookies
    const [cookies, _] = useCookies(["selectedParameters", "token"]);
    const location = useLocation();

    const handleClose = useCallback(() => {
        setLoading(false);
        setStatusMessage("warn");
        setIsToastVisible(false);
        setTextValidation("");
    }, []);

    useEffect(() => {
        const token: string = cookies.token || sessionStorage.getItem("token");

        findAllTimezoneListResponse(token).then(res => setTimezones(res.data))
    }, [cookies.token])

    useEffect(() => {
        const ref = toastRef;
        if (validationText && !isFieldsValid && !isToastVisible) {
            setToastRefToOpen(
                ref,
                validationText,
                "Atenção!",
                statusMessage,
                undefined,
                3000,
                false
            );
        }
    }, [validationText, isFieldsValid, statusMessage, isToastVisible]);

    const onUpdate = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const currentParameters: SelectedParametersType = cookies.selectedParameters;

        setLoading(true);
        const data: CustomerSettingsType = {
            showEvents,
            withSound,
            showDesktopEvents,
            notifyCnhExpireDate,
            sendAutomaticCommands,
            daysBefore,
            useIdleAlert,
            // Desligado, mantém os minutos salvos se o campo ficou inválido.
            idleAlertMinutes: useIdleAlert || isValidIdleMinutes(idleAlertMinutes)
                ? Number(idleAlertMinutes)
                : getCurrentUser?.configs?.customerSettings?.idleAlertMinutes ?? 5,
            useTimezone: getCurrentUser?.configs?.customerSettings?.useTimezone!!,
            timezoneId: getCurrentUser?.configs?.customerSettings?.timezoneId!!,
            customerId: currentParameters.customer.id,
            useVideoTelemetry: getCurrentUser.configs?.customerSettings?.useVideoTelemetry!!,
            useTurnstile: getCurrentUser.configs?.customerSettings?.useTurnstile!!,
            useCheckList: getCurrentUser.configs?.customerSettings?.useCheckList!!,
            useSpeedLimit: getCurrentUser?.configs?.customerSettings?.useSpeedLimit!!,
            useTransparentBackground: getCurrentUser?.configs?.customerSettings?.useTransparentBackground!!,
            useInterestPoint: getCurrentUser?.configs?.customerSettings?.useInterestPoint!!,
            useLocal: getCurrentUser?.configs?.customerSettings?.useLocal!!,
            speedLimit: getCurrentUser?.configs?.customerSettings?.speedLimit!!,
            lat: getCurrentUser?.configs?.customerSettings?.lat!!,
            lng: getCurrentUser?.configs?.customerSettings?.lng!!
        };

        if (useIdleAlert && !isValidIdleMinutes(idleAlertMinutes)) {
            setTextValidation("O alerta de tempo ocioso deve ser um número inteiro entre 1 e 1440 minutos.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        if (notifyCnhExpireDate && !daysBefore) {
            setTextValidation(
                "É necessário selecionar a antededência, selecione e tente novamente."
            );
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        try {
            await updateSettingsByCustomer(token, data);
            setTextValidation("Configurações atualizadas com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                window.location.reload();
            }, 1500);
        } catch (error: any) {
            const { data } = error.response

            setLoading(false);
            setFieldsIsValid(false);
            setStatusMessage("warn");
            setTextValidation(data.reason);
        }
    }, [
        showEvents,
        withSound,
        showDesktopEvents,
        notifyCnhExpireDate,
        sendAutomaticCommands,
        daysBefore,
        useIdleAlert,
        idleAlertMinutes,
        cookies.selectedParameters,
        getCurrentUser?.configs?.customerSettings,
        cookies.token,
        handleClose,
    ]);

    const handleOnValues = useCallback(() => {
        if (getCurrentUser.id !== -1) {
            setShowEvents(getCurrentUser?.configs?.customerSettings?.showEvents!!);
            setWithSound(getCurrentUser?.configs?.customerSettings?.withSound!!);
            setShowDeskTopEvents(getCurrentUser?.configs?.customerSettings?.showDesktopEvents!!);
            setNotifyCnhExpireDate(getCurrentUser?.configs?.customerSettings?.notifyCnhExpireDate!!);
            setSendAutomaticCommands(getCurrentUser?.configs?.customerSettings?.sendAutomaticCommands!!)
            setDaysBefore(String(getCurrentUser?.configs?.customerSettings?.daysBefore!!));
            setUseIdleAlert(getCurrentUser?.configs?.customerSettings?.useIdleAlert ?? false);
            setIdleAlertMinutes(String(getCurrentUser?.configs?.customerSettings?.idleAlertMinutes ?? 5));

            setUseLocalMap(getCurrentUser?.configs?.customerSettings?.useLocal!!);
            setUseTransparentBackground(getCurrentUser?.configs?.customerSettings?.useTransparentBackground!!);
            setUseInterestPoint(getCurrentUser?.configs?.customerSettings?.useInterestPoint!!);
            setUseTimezone(getCurrentUser?.configs?.customerSettings?.useTimezone!!);
            setTimezoneId(getCurrentUser?.configs?.customerSettings?.timezoneId!!);
            setUseSpeedLimit(getCurrentUser?.configs?.customerSettings?.useSpeedLimit!!);
            setSpeedLimit(getCurrentUser?.configs?.customerSettings?.speedLimit!!);
            setLocalLat(getCurrentUser?.configs?.customerSettings?.lat!!);
            setLocalLng(getCurrentUser?.configs?.customerSettings?.lng!!);
        }
    }, [getCurrentUser?.configs?.customerSettings, getCurrentUser.id]);

    useEffect(() => {
        handleOnValues();
    }, [handleOnValues]);

    const verifyChanges = useCallback(() => {
        const customerSettings = getCurrentUser?.configs?.customerSettings;

        if (
            showEvents !== customerSettings?.showEvents ||
            withSound !== customerSettings?.withSound ||
            showDesktopEvents !== customerSettings?.showDesktopEvents ||
            notifyCnhExpireDate !== customerSettings?.notifyCnhExpireDate ||
            sendAutomaticCommands !== customerSettings?.sendAutomaticCommands ||
            daysBefore !== String(customerSettings?.daysBefore) ||
            useIdleAlert !== (customerSettings?.useIdleAlert ?? false) ||
            idleAlertMinutes !== String(customerSettings?.idleAlertMinutes ?? 5)
        ) {
            setHasChanges(true);
        } else {
            setHasChanges(false);
        }
    }, [
        getCurrentUser?.configs?.customerSettings,
        showEvents,
        withSound,
        showDesktopEvents,
        notifyCnhExpireDate,
        sendAutomaticCommands,
        daysBefore,
        useIdleAlert,
        idleAlertMinutes
    ]);

    useEffect(() => {
        verifyChanges();
    }, [verifyChanges]);

    const getPlacesPredictions = useCallback(async () => {
        try {
            const response = await findAllPlacesWithIqLocation(query);
            setPredictionResults(response.data);
        } catch (error) {
            console.log(error);
            setPredictionResults([]);
        }
    }, [query]);

    useEffect(() => {
        if (canSeach) {
            getPlacesPredictions();
        }
        setCanSearch(false);
    }, [canSeach, getPlacesPredictions]);

    const saveMapSettings = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const currentParameters: SelectedParametersType = cookies.selectedParameters;

        const data: CustomerSettingsType = {
            showEvents: getCurrentUser?.configs?.customerSettings?.showEvents!!,
            withSound: getCurrentUser?.configs?.customerSettings?.withSound!!,
            showDesktopEvents: getCurrentUser?.configs?.customerSettings?.showDesktopEvents!!,
            notifyCnhExpireDate: getCurrentUser?.configs?.customerSettings?.notifyCnhExpireDate!!,
            sendAutomaticCommands: getCurrentUser?.configs?.customerSettings?.sendAutomaticCommands!!,
            daysBefore: getCurrentUser?.configs?.customerSettings?.daysBefore!!,
            useIdleAlert: getCurrentUser?.configs?.customerSettings?.useIdleAlert ?? false,
            idleAlertMinutes: getCurrentUser?.configs?.customerSettings?.idleAlertMinutes ?? 5,
            customerId: currentParameters.customer.id,
            useVideoTelemetry: getCurrentUser.configs?.customerSettings?.useVideoTelemetry!!,
            useTurnstile: getCurrentUser.configs?.customerSettings?.useTurnstile!!,
            useCheckList: getCurrentUser.configs?.customerSettings?.useCheckList!!,
            useSpeedLimit: useSpeedLimit,
            useLocal: useLocalMap,
            useTransparentBackground: useTransparentBackground,
            useInterestPoint: useInterestPoint,
            useTimezone: useTimezone,
            timezoneId: timezoneId,
            speedLimit: speedLimit,
            lat: localLat,
            lng: localLng
        };

        try {
            await updateSettingsByCustomer(token, data);
            setTextValidation("Configurações atualizadas com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                window.location.reload();
            }, 1500);
        } catch (error: any) {
            const { data } = error.response

            setLoading(false);
            setFieldsIsValid(false);
            setStatusMessage("warn");
            setTextValidation(data.reason);
        }
    }, [cookies.token, cookies.selectedParameters, getCurrentUser.configs?.customerSettings?.showEvents, getCurrentUser.configs?.customerSettings?.withSound, getCurrentUser.configs?.customerSettings?.showDesktopEvents, getCurrentUser.configs?.customerSettings?.notifyCnhExpireDate, getCurrentUser.configs?.customerSettings?.sendAutomaticCommands, getCurrentUser.configs?.customerSettings?.daysBefore, getCurrentUser.configs?.customerSettings?.useVideoTelemetry, getCurrentUser.configs?.customerSettings?.useTurnstile, getCurrentUser.configs?.customerSettings?.useCheckList, useSpeedLimit, useLocalMap, useTransparentBackground, useInterestPoint, useTimezone, timezoneId, speedLimit, localLat, localLng, handleClose]);

    const markerIcon = useCallback((type: string) => icon({
        iconSize: [35, 35],
        iconAnchor: [10, 41],
        popupAnchor: [2, -40],
        iconUrl: require(`../../../assets/files/cars/png/${formatString(type)}.png`),
        shadowUrl: ""
    }), []);

    const SettingsModal = useMemo(() => {
        return <Rmodal open={modalVisible && !loading} toast={
            <div style={{ top: 0, height: 50, width: 50, position: "absolute", zIndex: 99999 }}>
                <Toast ref={toastModalRef} position="top-right" onShow={() => {
                    setFieldsIsValid(true);
                    setIsToastVisible(true);
                    if (validationText) {
                        setTimeout(() => {
                            setTextValidation("");
                            setIsToastVisible(false);
                        }, 3000);
                    }
                }} />
            </div>
        }>
            <Paper
                className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-shadow-2xl tw-transition-all tw-duration-300 tw-ease-gtr-smooth"
                sx={{
                    ...style,
                    display: "flex",
                    flexDirection: "column",
                    width: matches ? "calc(100vw - 24px)" : "min(92vw, 850px)",
                    maxWidth: 850,
                    maxHeight: "calc(100dvh - 24px)",
                    overflow: "hidden",
                    p: 0,
                    borderRadius: 4,
                    boxShadow: "0 24px 70px rgba(0, 0, 0, 0.28)",
                }}
            >
                <Box
                    className="tw-flex tw-flex-none tw-items-center tw-justify-between tw-bg-gtr-sidebar tw-px-5 tw-py-4 tw-text-white"
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: { xs: 2, sm: 2.5 }, backgroundImage: 'linear-gradient(to right, #006C77, #09414A)' }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#fff', display: 'flex', alignItems: 'center', gap: 1, fontFamily: 'Prometo' }}>
                        <SettingsOutlined /> Parâmetros do Sistema
                    </Typography>
                    <Tooltip title="Fechar" arrow>
                        <IconButton aria-label="Fechar parâmetros do sistema" onClick={() => setModalVisible(false)} size="small" sx={{ color: '#fff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' } }}>
                            <CloseOutlined />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Grid
                    className="tw-flex-1 tw-overflow-y-auto tw-p-4 sm:tw-p-6"
                    container
                    spacing={3}
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        padding: { xs: 2, sm: 3 },
                        scrollbarWidth: "thin",
                        scrollbarColor: "#006C77 transparent",
                        "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#ffff" },
                        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,63,74,0.24)", borderWidth: "1px" },
                        "& .MuiOutlinedInput-root:not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline": { borderColor: "#006C77", borderWidth: "1px" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#00B700" },
                        "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,183,0,0.12)" },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#00B700", borderWidth: "2px" },
                        "& .MuiCheckbox-root": { color: "#006C77" },
                        "& .MuiCheckbox-root.Mui-checked": { color: "#00B700" },
                    }}
                >
                    {/* LADO ESQUERDO: CONFIGURAÇÕES */}
                    <Grid item xs={12} md={5}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Grupo: Visualização */}
                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', letterSpacing: 1 }}>INTERFACE E MAPA</Typography>
                                <Paper
                                    className="tw-rounded-xl tw-border-0 tw-bg-slate-50 tw-shadow-none"
                                    elevation={0}
                                    sx={{ p: 1.5, mt: 1, display: 'flex', flexDirection: 'column', gap: 1, backgroundColor: '#f9fafb', border: 'none', borderRadius: 3, boxShadow: 'none' }}
                                >
                                    <FormControlLabel
                                        control={<Checkbox size="small" checked={useTransparentBackground} onChange={(e) => setUseTransparentBackground(e.target.checked)} />}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>Usar fundo transparente nos veículos</Typography>
                                                <Tooltip title="Remove o card branco de fundo das placas no mapa, deixando apenas o texto visível.">
                                                    <HelpOutlineOutlined sx={{ fontSize: 20, color: 'text.secondary', cursor: 'help' }} />
                                                </Tooltip>
                                            </Box>
                                        }
                                    />
                                    <FormControlLabel
                                        control={<Checkbox size="small" checked={useLocalMap} onChange={(e) => setUseLocalMap(e.target.checked)} />}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>Iniciar mapa em local fixo</Typography>
                                                <Tooltip title="Faz com que o mapa sempre inicie na posição geográfica definida abaixo ao abrir o sistema.">
                                                    <HelpOutlineOutlined sx={{ fontSize: 20, color: 'text.secondary', cursor: 'help' }} />
                                                </Tooltip>
                                            </Box>
                                        }
                                    />
                                    <FormControlLabel
                                        control={<Checkbox size="small" checked={useInterestPoint} onChange={(e) => setUseInterestPoint(e.target.checked)} />}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>Utilizar Pontos de Interesse</Typography>
                                                <Tooltip title="O módulo de Pontos de Interesse permite cadastrar locais importantes para visualização no mapa">
                                                    <HelpOutlineOutlined sx={{ fontSize: 20, color: 'text.secondary', cursor: 'help' }} />
                                                </Tooltip>
                                            </Box>
                                        }
                                    />
                                </Paper>
                            </Box>

                            {/* Grupo: Limites */}
                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', letterSpacing: 1 }}>ALERTAS E LIMITES</Typography>
                                <Paper
                                    className="tw-rounded-xl tw-border-0 tw-bg-slate-50 tw-shadow-none"
                                    elevation={0}
                                    sx={{ p: 1.5, mt: 1, display: 'flex', flexDirection: 'column', gap: 2, backgroundColor: '#f9fafb', border: 'none', borderRadius: 3, boxShadow: 'none' }}
                                >
                                    <FormControlLabel
                                        control={<Checkbox size="small" checked={useSpeedLimit} onChange={(e) => setUseSpeedLimit(e.target.checked)} />}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>Monitorar limite de velocidade</Typography>
                                                <Tooltip title="Define um limite de velocidade global para relatórios de velocidade.">
                                                    <HelpOutlineOutlined sx={{ fontSize: 20, color: 'text.secondary', cursor: 'help' }} />
                                                </Tooltip>
                                            </Box>
                                        }
                                    />
                                    <TextField
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                Velocidade Máxima (KM/H)
                                                <Tooltip title="Velocidade máxima permitida para todos os veículos da frota.">
                                                    <HelpOutlineOutlined sx={{ fontSize: 20, color: 'text.secondary', cursor: 'help' }} />
                                                </Tooltip>
                                            </Box>
                                        }
                                        type="number"
                                        size="small"
                                        fullWidth
                                        disabled={!useSpeedLimit}
                                        value={speedLimit}
                                        onChange={(e) => setSpeedLimit(Number(e.target.value))}
                                        InputLabelProps={{ shrink: true }}
                                    />

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={useTimezone}
                                                onChange={(event) => {
                                                    setUseTimezone(event.target.checked);
                                                }}
                                            />
                                        }
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                                                    Utilizar fuso horário personalizado
                                                </Typography>
                                                <Tooltip title="Permite que relatórios e consultas utilizem o fuso horário selecionado abaixo.">
                                                    <HelpOutlineOutlined
                                                        sx={{
                                                            fontSize: 20,
                                                            color: 'text.secondary',
                                                            cursor: 'help'
                                                        }}
                                                    />
                                                </Tooltip>
                                            </Box>
                                        }
                                    />

                                    <Autocomplete
                                        size="small"
                                        fullWidth
                                        disabled={!useTimezone}
                                        options={timezones}
                                        value={timezones.find((item) => item.id === timezoneId) ?? null}
                                        onChange={(_, option) => {
                                            setTimezoneId(option?.id ?? null);
                                        }}
                                        getOptionLabel={(option) => `${option.name} - ${option.description?.split(".")[0]}`}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        noOptionsText="Nada encontrado"
                                        renderOption={(props, option) => (
                                            <li {...props}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        width: "100%",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: 13 }}>
                                                        {`${option.name} - ${option.description?.split(".")[0]}`}
                                                    </Typography>

                                                    <Tooltip
                                                        title={option.description}
                                                        arrow
                                                        placement="right"
                                                    >
                                                        <HelpOutlineOutlined
                                                            sx={{
                                                                fontSize: 18,
                                                                color: "text.secondary",
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </Box>
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Fuso horário"
                                                size="small"
                                                placeholder="Selecione um fuso horário"
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    />
                                </Paper>
                            </Box>
                        </Box>
                    </Grid>

                    {/* LADO DIREITO: MAPA E BUSCA */}
                    <Grid item xs={12} md={7}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', letterSpacing: 1 }}>
                                {useLocalMap ? "DEFINIR LOCALIZAÇÃO INICIAL" : "PRÉVIA VISUAL DO MAPA"}
                            </Typography>

                            <Autocomplete
                                size="small"
                                disabled={!useLocalMap}
                                options={predictionResults}
                                getOptionLabel={(option) => option.display_name}
                                noOptionsText="Sem resultados"
                                renderInput={(params) => <TextField {...params} label="Buscar endereço para o mapa" />}
                                onInputChange={(e, val) => {
                                    clearTimeout(typingTimer);
                                    setQuery(val);
                                }}
                                onKeyUp={() => {
                                    clearTimeout(typingTimer);
                                    setTypingTimer(setTimeout(() => query && setCanSearch(true), 1500));
                                }}
                                onChange={(e, option) => {
                                    if (option) {
                                        mapRef.current?.flyTo([Number(option.lat), Number(option.lon)], 18);
                                        setLocalLat(Number(option.lat));
                                        setLocalLng(Number(option.lon));
                                    }
                                }}
                                sx={{ backgroundColor: '#fff' }}
                            />

                            <Paper className="tw-overflow-hidden tw-rounded-xl tw-border-black/10" variant="outlined" sx={{ height: 320, width: '100%', overflow: 'hidden', borderRadius: 3, position: 'relative' }}>
                                <MapContainer
                                    center={{ lat: localLat, lng: localLng }}
                                    zoom={18}
                                    dragging={useLocalMap}
                                    scrollWheelZoom={useLocalMap}
                                    doubleClickZoom={useLocalMap}
                                    ref={(ref) => mapRef.current = ref}
                                    style={{ height: '100%', width: '100%' }}
                                >
                                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                    <Marker position={{ lat: localLat, lng: localLng }} icon={markerIcon("carro")}>
                                        <LeafletTooltip
                                            className={useTransparentBackground ? "tooltip-transparent" : ""}
                                            permanent
                                            direction="top"
                                            offset={[0, -20]}
                                        >
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                background: useTransparentBackground ? 'transparent' : '#fff',
                                                padding: useTransparentBackground ? 0 : '4px 8px',
                                                borderRadius: '4px',
                                                boxShadow: useTransparentBackground ? 'none' : '0 1px 3px rgba(0,0,0,0.2)'
                                            }}>
                                                {!useTransparentBackground && (
                                                    <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: 'green', border: '1px solid white' }} />
                                                )}
                                                <Typography sx={{
                                                    fontSize: 11,
                                                    fontWeight: 'bold',
                                                    color: useTransparentBackground ? 'red' : '#333',
                                                    textShadow: useTransparentBackground ? '1px 1px 0 #fff, -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff' : 'none'
                                                }}>
                                                    ABC-1234
                                                </Typography>
                                            </Box>
                                        </LeafletTooltip>
                                    </Marker>
                                </MapContainer>
                                {!useLocalMap && (
                                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.1)', zIndex: 1000, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {/* Overlay opcional para indicar que o mapa é apenas prévia se useLocalMap for false */}
                                    </Box>
                                )}
                            </Paper>
                        </Box>
                    </Grid>
                </Grid>

                <Box
                    className="tw-flex tw-flex-none tw-flex-col-reverse tw-gap-2 tw-border-t tw-border-black/10 tw-bg-white tw-p-4 sm:tw-flex-row sm:tw-justify-end"
                    sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: { sm: 'flex-end' }, gap: 1, padding: 2, borderTop: '1px solid rgba(0,0,0,0.10)', backgroundColor: '#ffff' }}
                >
                    <Button
                        variant="contained"
                        startIcon={<CloseOutlined />}
                        onClick={() => setModalVisible(false)}
                        sx={{
                            minWidth: 132,
                            minHeight: 44,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 'bold',
                            fontFamily: 'Prometo',
                            backgroundColor: '#dc3545',
                            boxShadow: 'none',
                            transition: 'transform 200ms ease, box-shadow 200ms ease',
                            '&:hover': { backgroundColor: '#dc3545', boxShadow: '0 8px 18px rgba(220,53,69,0.22)', transform: 'translateY(-1px)' }
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        disabled={isImpersonationLocked()}
                        startIcon={<SaveOutlined />}
                        onClick={saveMapSettings}
                        sx={{
                            minWidth: 132,
                            minHeight: 44,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 'bold',
                            fontFamily: 'Prometo',
                            backgroundColor: '#00B700',
                            boxShadow: 'none',
                            transition: 'transform 200ms ease, box-shadow 200ms ease',
                            '&:hover': { backgroundColor: '#009900', boxShadow: '0 8px 18px rgba(0,183,0,0.22)', transform: 'translateY(-1px)' }
                        }}
                    >
                        Salvar
                    </Button>
                </Box>
            </Paper>
        </Rmodal>
    }, [modalVisible, loading, matches, useTransparentBackground, useLocalMap, useInterestPoint, useSpeedLimit, speedLimit, useTimezone, timezones, predictionResults, localLat, localLng, markerIcon, saveMapSettings, validationText, timezoneId, typingTimer, query]);

    const Template = useMemo(() => (
        <Box
            className="grid-box-out-container tw-flex tw-min-h-screen tw-w-full tw-items-center tw-justify-center tw-bg-transparent tw-p-0.5"
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                minHeight: "100vh",
                padding: 2,
                background: "transparent",
            }}
        >
            <Toast
                ref={toastRef}
                position="center"
                onShow={() => {
                    setIsToastVisible(true);

                    if (validationText) {
                        setTimeout(() => {
                            setFieldsIsValid(true);
                            setIsToastVisible(false);
                        }, 3000);
                    }
                }}
            />
            <Box
                className="grid-container settings-page container container-fluid tw-w-full tw-overflow-hidden tw-rounded-xl tw-border-0 tw-bg-white tw-p-2.5 tw-shadow-md"
                style={{
                    marginTop: 0,
                    padding: 10,
                    background: "#ffff",
                    transition: "all 0.3s ease-out allow-discrete",
                    width: matches ? "100%" : (isHiddenMenu ? "70vw" : "65vw"),
                    borderRadius: 12,
                    position: "relative"
                    // height: 650
                }}
            >
                <Toolbar
                    className="grid-header-bar tw-rounded-xl tw-bg-gtr-sidebar tw-text-white tw-shadow-sm"
                    style={{
                        display: "grid",
                        backgroundImage: "linear-gradient(to right, #09414A , #09414A)",
                        fontFamily: "Prometo",
                        marginTop: 10,
                        marginBottom: 10,
                        borderRadius: 12,
                        color: "#ffff",
                        position: "relative"
                    }}
                >
                    <Box
                        className="header-actions row"
                        sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            alignContent: "center",
                            fontFamily: "Prometo",
                            width: "100%"
                        }}
                    >
                        <Box
                            className="header-box-title col-sm-12 col-md-11 m-sm-1 m-md-0"
                            sx={{
                                padding: 1,
                                transition: "all 0.2s ease-out allow-discrete",
                            }}
                        >
                            <Typography>Configurações</Typography>
                        </Box>
                    </Box>
                    <Tooltip title="Parâmetros" arrow>
                        <IconButton
                            aria-label="Abrir parâmetros do sistema"
                            onClick={() => {
                                setModalVisible(true);
                            }}
                            sx={{ position: "absolute", right: 50, top: "50%", transform: "translateY(-50%)", color: "#ffff", transition: "background-color 0.2s ease-out", "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" } }}
                        >
                            <SettingsOutlined />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Fechar" arrow>
                        <IconButton
                            aria-label="Fechar configurações"
                            onClick={() => {
                                window.location.replace("/")
                            }}
                            sx={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#ffff", transition: "background-color 0.2s ease-out", "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" } }}
                        >
                            <CloseOutlined />
                        </IconButton>
                    </Tooltip>
                </Toolbar>

                <Box
                    className="settings-content-shell"
                    style={{
                        marginTop: 2,
                        borderRadius: 5,
                        backgroundColor: "transparent",
                        fontFamily: "Prometo",
                    }}
                >
                    {!loading ? (
                            <Box
                                className="tw-grid tw-grid-cols-1 tw-rounded-xl tw-bg-slate-50 tw-p-3 md:tw-grid-cols-2 sm:tw-p-5"
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                                    columnGap: { xs: 0, md: 3 },
                                    rowGap: 0.5,
                                    padding: { xs: 1.5, sm: 2.5 },
                                    backgroundColor: "#f9fafb",
                                    margin: 0,
                                    borderRadius: 3,
                                    "& > .col-md-12, & > .col-md-4": {
                                        width: "100%",
                                        maxWidth: "none",
                                        margin: "0 !important",
                                        padding: 0,
                                    },
                                    "& .MuiFormControlLabel-root": {
                                        width: "100%",
                                        minHeight: 50,
                                        margin: "0 !important",
                                        padding: "6px 8px",
                                        border: "none",
                                        borderRadius: 1.5,
                                        backgroundColor: "transparent",
                                        transition: "background-color 200ms ease",
                                    },
                                    "& .MuiFormControlLabel-root:hover": {
                                        backgroundColor: "rgba(255,255,255,0.56)",
                                    },
                                    "& .MuiFormControlLabel-label": { width: "100%", fontFamily: "Prometo", fontSize: 14 },
                                    "& .MuiCheckbox-root": { color: "#006C77" },
                                    "& .MuiCheckbox-root.Mui-checked": { color: "#00B700" },
                                    "& > .col-md-4 .MuiFormControl-root": { width: "100%" },
                                    "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#ffff" },
                                    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,63,74,0.24)", borderWidth: "1px" },
                                    "& .MuiOutlinedInput-root:not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline": { borderColor: "#006C77", borderWidth: "1px" },
                                    "& .MuiInputLabel-root.Mui-focused": { color: "#00B700" },
                                    "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,183,0,0.12)" },
                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#00B700", borderWidth: "2px" },
                                }}
                            >
                            {SettingsModal}
                            {/*content */}
                            <Box className="col-md-12" style={{ margin: "15px 0px 5px 0px" }}>
                                <FormControlLabel
                                    style={{ marginRight: 5, marginLeft: 5 }}
                                    control={
                                        <Checkbox
                                            disabled={!location?.state?.canEdit}
                                            checked={showEvents}
                                            onChange={(event) => {
                                                setShowEvents(event.target.checked);
                                            }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            Mostrar eventos
                                            <Tooltip title="Exibe alertas em tempo real sobre ignição, velocidade e outros eventos na barra lateral.">
                                                <HelpOutlineOutlined sx={{ fontSize: 20, color: 'text.secondary', cursor: 'help' }} />
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                            </Box>

                            <Box className="col-md-12" style={{ margin: "15px 0px 5px 0px" }}>
                                <FormControlLabel
                                    style={{ marginRight: 5, marginLeft: 5 }}
                                    control={
                                        <Checkbox
                                            disabled={!location?.state?.canEdit}
                                            checked={withSound}
                                            onChange={(event) => {
                                                setWithSound(event.target.checked);
                                            }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            Emitir som nas notificações
                                            <Tooltip title="Emite um alerta sonoro sempre que um novo evento for detectado pelo sistema.">
                                                <HelpOutlineOutlined sx={{ fontSize: 20, color: 'text.secondary', cursor: 'help' }} />
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                            </Box>

                            <Box className="col-md-12" style={{ margin: "15px 0px 5px 0px" }}>
                                <FormControlLabel
                                    style={{ marginRight: 5, marginLeft: 5 }}
                                    control={
                                        <Checkbox
                                            disabled={!location?.state?.canEdit}
                                            checked={showDesktopEvents}
                                            onChange={(event) => {
                                                setShowDeskTopEvents(event.target.checked);
                                            }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            Mostrar notificações desktop
                                            <Tooltip title="Exibe notificações flutuantes do Windows mesmo com o navegador minimizado.">
                                                <HelpOutlineOutlined sx={{ fontSize: 20, color: 'text.secondary', cursor: 'help' }} />
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                            </Box>

                            <Box className="col-md-12" style={{ margin: "15px 0px 5px 0px" }}>
                                <FormControlLabel
                                    style={{ marginRight: 5, marginLeft: 5 }}
                                    control={
                                        <Checkbox
                                            disabled={!location?.state?.canEdit}
                                            checked={sendAutomaticCommands}
                                            onChange={(event) => {
                                                setSendAutomaticCommands(event.target.checked);
                                            }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            Enviar comandos automáticos
                                            <Tooltip title="Habilita o envio automático de comandos pré-configurados em situações específicas.">
                                                <HelpOutlineOutlined sx={{ fontSize: 20, color: 'text.secondary', cursor: 'help' }} />
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                            </Box>

                            <Box className="col-md-12" style={{ margin: "15px 0px 5px 0px" }}>
                                <FormControlLabel
                                    style={{ marginRight: 5, marginLeft: 5 }}
                                    disabled={!location?.state?.canEdit}
                                    control={
                                        <Checkbox
                                            disabled={!location?.state?.canEdit}
                                            checked={notifyCnhExpireDate}
                                            onChange={(event) => {
                                                setNotifyCnhExpireDate(event.target.checked);
                                            }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            Notificar o vencimento da CNH dos motoristas
                                            <Tooltip title="Monitora a validade da CNH dos motoristas e gera alertas de proximidade do vencimento.">
                                                <HelpOutlineOutlined sx={{ fontSize: 20, color: 'text.secondary', cursor: 'help' }} />
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                            </Box>
                            <Box
                                className="col-md-4"
                                style={{ margin: "15px 0px 20px 15px" }}
                            >
                                <FormControl className="form-group" style={{ display: "flex" }}>
                                    <InputLabel
                                        id="category-label"
                                        style={{ color: "black", fontFamily: "Prometo" }}
                                        shrink
                                        disabled={!location?.state?.canEdit || !notifyCnhExpireDate}
                                    >
                                        Antecedência
                                        <Tooltip title="Define quantos dias antes do vencimento o sistema deve começar a alertar.">
                                            <HelpOutlineOutlined sx={{ fontSize: 20, color: 'text.secondary', ml: 0.5, cursor: 'help' }} />
                                        </Tooltip>
                                    </InputLabel>
                                    <Select
                                        id="chip-label-select-category"
                                        name="daysBefore"
                                        autoComplete="off"
                                        className="form-control input-select-category"
                                        labelId="chip-label-select-category"
                                        label={"Antecedência  "}
                                        disabled={!location?.state?.canEdit || !notifyCnhExpireDate}
                                        style={{
                                            width: "100%",
                                            backgroundColor: "#ffffff29",
                                            borderColor: "lightgray",
                                        }}
                                        displayEmpty
                                        input={
                                            <OutlinedInput
                                                id="select-chip-select-category"
                                                label={"Antecedência  "}
                                                notched
                                            />
                                        }
                                        renderValue={(item) => (
                                            <Chip
                                                key={item}
                                                style={{
                                                    color: "black",
                                                    width: 200,
                                                    fontFamily: "Prometo",
                                                }}
                                                label={item ? `${item} dias` : "Selecione a antecedência"}
                                            />
                                        )}
                                        value={daysBefore}
                                        onChange={(event: SelectChangeEvent) => {
                                            setDaysBefore(event.target.value);
                                        }}
                                    >
                                        {[
                                            { id: 1, name: "7" },
                                            { id: 2, name: "15" },
                                            { id: 3, name: "30" },
                                        ].map((item, i) => (
                                            <MenuItem key={item.id + i} value={item.name}>
                                                {`${item.name} dias`}
                                            </MenuItem>
                                        ))}

                                        <MenuItem key={"noselect"} value={""}>
                                            Selecione a antecedência
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            <Box className="col-md-12" style={{ margin: "15px 0px 5px 0px" }}>
                                <FormControlLabel
                                    style={{ marginRight: 5, marginLeft: 5 }}
                                    disabled={!location?.state?.canEdit}
                                    control={
                                        <Checkbox
                                            disabled={!location?.state?.canEdit}
                                            checked={useIdleAlert}
                                            onChange={(event) => {
                                                setUseIdleAlert(event.target.checked);
                                            }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            Alertar tempo ocioso dos veículos
                                            <Tooltip title="Gera alerta no sino quando um veículo fica com a ignição ligada e parado por mais tempo que o limite. O limite também define quando o veículo fica laranja na grade (desligado, a grade usa 5 minutos).">
                                                <HelpOutlineOutlined sx={{ fontSize: 20, color: 'text.secondary', cursor: 'help' }} />
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                            </Box>
                            <Box
                                className="col-md-4"
                                style={{ margin: "15px 0px 20px 15px" }}
                            >
                                <TextField
                                    fullWidth
                                    type="number"
                                    autoComplete="off"
                                    disabled={!location?.state?.canEdit || !useIdleAlert}
                                    label="Tempo ocioso (minutos)"
                                    InputLabelProps={{ shrink: true, style: { color: "black", fontFamily: "Prometo" } }}
                                    inputProps={{ min: 1, max: 1440, step: 1 }}
                                    value={idleAlertMinutes}
                                    onChange={(event) => setIdleAlertMinutes(event.target.value)}
                                />
                            </Box>
                        </Box>
                    ) : (
                        <div
                            style={{
                                width: "100%",
                                padding: 20,
                                display: "flex",
                                alignItems: "center",
                                flexDirection: "column",
                            }}
                        >
                            <div className="tracker-loading">
                                <div className="ld-tracker">
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                </div>
                            </div>
                        </div>
                    )}
                    <Box
                        className="tw-flex tw-justify-end tw-border-t tw-border-black/10 tw-bg-white tw-p-4"
                        sx={{ display: "flex", justifyContent: "flex-end", padding: 2, borderTop: "1px solid rgba(0,0,0,0.10)", backgroundColor: "#ffff" }}
                    >
                        <Paper
                            className="tw-flex tw-w-full tw-flex-col-reverse tw-gap-2 sm:tw-w-auto sm:tw-flex-row"
                            elevation={0}
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column-reverse", sm: "row" },
                                justifyContent: "flex-end",
                                width: { xs: "100%", sm: "auto" },
                                marginLeft: "auto",
                                alignSelf: "flex-end",
                                gap: 1,
                                background: "transparent",
                            }}
                        >
                            <Button
                                type="button"
                                style={{
                                    fontFamily: "Prometo",
                                    color: "#ffff",
                                    marginRight: 5,
                                }}
                                disabled={!hasChanges}
                                onClick={() => {
                                    if (!isToastVisible) {
                                        handleClose();
                                        handleOnValues();
                                    }
                                }}
                                className="btn btn-primary btn-block login-button-close tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            >
                                <CloseOutlined style={{ marginRight: 5 }} />
                                <Typography
                                    className="forgot-password"
                                    style={{
                                        fontFamily: "Prometo",
                                        fontSize: "16px",
                                        color: "#ffff",
                                    }}
                                >
                                    Cancelar
                                </Typography>
                            </Button>
                            <Button
                                type="button"
                                style={{
                                    fontFamily: "Prometo",
                                    color: "#ffff",
                                    backgroundColor: "#00B700",
                                    borderRadius: "5px",
                                    padding: "8px 24px",
                                    boxShadow: "none"
                                }}
                                disabled={!hasChanges || isImpersonationLocked()}
                                onClick={() => {
                                    onUpdate();
                                }}
                                className="btn btn-primary btn-block login-button tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            >
                                <SaveOutlined style={{ marginRight: 5 }} />
                                <Typography
                                    className="forgot-password"
                                    style={{
                                        fontFamily: "Prometo",
                                        fontSize: "16px",
                                        color: "#ffff",
                                        textTransform: "none",
                                        fontWeight: "bold"
                                    }}
                                >
                                    Salvar
                                </Typography>
                            </Button>
                        </Paper>
                    </Box>
                </Box>
            </Box>
        </Box>
    ), [matches, isHiddenMenu, loading, SettingsModal, location?.state?.canEdit, showEvents, withSound, showDesktopEvents, sendAutomaticCommands, notifyCnhExpireDate, daysBefore, useIdleAlert, idleAlertMinutes, hasChanges, validationText, isToastVisible, handleClose, handleOnValues, onUpdate]);

    return Template;
};

export default SettingsPage;
